import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { trackCTAClick, trackAction } from '../config/gtm';

interface NavbarProps {
    currentTheme: number;
    onThemeChange: (theme: number) => void;
}

const Navbar: React.FC<NavbarProps> = ({ currentTheme, onThemeChange }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { language, setLanguage } = useLanguage();

    // Verificar si estamos en la página de inicio
    const isHomePage = location.pathname === '/';

    // Función simple de traducción
    const t = (es: string, en: string) => language === 'es' ? es : en;

    const books = [
        { id: 2, icon: '👑', label: t('El nuevo traje del rey', "The King's New Clothes") },
        { id: 3, icon: '🐰', label: t('La liebre y la tortuga', 'The Hare and the Tortoise') },
        { id: 4, icon: '🐭', label: t('El ratón de campo y el ratón de ciudad', 'The Town Mouse and the Country Mouse') },
        { id: 5, icon: '🦆', label: t('El patito feo', 'The Ugly Duckling') },
        { id: 6, icon: '🥣', label: t('Sopa de piedra', 'Stone Soup') },
        { id: 7, icon: '🐷', label: t('Los tres cerditos', 'The Three Little Pigs') },
        { id: 8, icon: '🐻', label: t('Ricitos de oro y los tres osos', 'Goldilocks and the Three Bears') },
        { id: 9, icon: '🥚', label: t('La gallina de los huevos de oro', 'The Goose That Laid the Golden Eggs') }
    ];

    const innovationsSections = [
        { 
            id: 'filosofia', 
            icon: '🧠', 
            label: t('Filosofía Princonser', 'Princonser Philosophy'),
            path: '/innovaciones?section=1'
        },
        { 
            id: 'curriculum', 
            icon: '📚', 
            label: t('Currículum Holístico', 'Holistic Curriculum'),
            path: '/innovaciones?section=2'
        },
        { 
            id: 'metodo', 
            icon: '🎯', 
            label: t('Método de Enseñanza', 'Teaching Method'),
            path: '/innovaciones?section=3'
        },
        { 
            id: 'gamificacion', 
            icon: '🎮', 
            label: t('Gamificación', 'Gamification'),
            path: '/innovaciones?section=4'
        },
        { 
            id: 'ecosistema', 
            icon: '🌱', 
            label: t('Ecosistema Incentivos', 'Incentive Ecosystem'),
            path: '/innovaciones?section=5'
        }
    ];

    const handleThemeChange = (theme: number) => {
        // Tracking con GTM
        const bookNames = {
            2: 'El Traje Nuevo del Rey',
            3: 'La Liebre y la Tortuga',
            4: 'El Ratón de Campo y el Ratón de Ciudad',
            5: 'El Patito Feo',
            6: 'Sopa de Piedra',
            7: 'Los Tres Cerditos',
            8: 'Hansel y Gretel',
            9: 'La Gallina de los Huevos de Oro'
        };
        
        const bookName = bookNames[theme as keyof typeof bookNames] || 'Unknown Book';
        trackAction('book_navigation', 'navbar', bookName);
        
        if (isHomePage) {
            // Si estamos en el Home, cambiar el tema
            onThemeChange(theme);
        } else {
            // Si no estamos en el Home, navegar a la página del libro correspondiente
            const bookRoutes = {
                2: '/libros/1',
                3: '/libros/2', 
                4: '/libros/3',
                5: '/libros/4',
                6: '/libros/5',
                7: '/libros/6',
                8: '/libros/7',
                9: '/libros/8'
            };
            const route = bookRoutes[theme as keyof typeof bookRoutes];
            if (route) {
                navigate(route);
            }
        }
        setIsMenuOpen(false);
    };

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 border-b-3 border-white ${
            isHomePage && currentTheme === 1
                ? 'bg-gradient-to-r from-red-500 via-teal-400 to-blue-500 text-white'
                : 'bg-white/90 text-gray-800'
        }`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-14 sm:h-16">
                    {/* Logo */}
                    <div className="flex items-center">
                        <h1 
                            onClick={() => navigate('/')}
                            className={`text-xl sm:text-2xl font-bold font-['Dancing_Script'] cursor-pointer transition-all duration-300 hover:scale-105 ${
                                isHomePage && currentTheme === 1 ? 'text-white' : 'text-blue-600'
                            }`}
                        >
                            Princonser Kids
                        </h1>
                    </div>

                    {/* Navegación Desktop - Oculto en móviles */}
                    <div className="hidden lg:flex items-center space-x-3">
                        {/* Botón Menú */}
                        <div className="relative">
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className={`
                                    relative group
                                    px-4 py-2
                                    rounded-full
                                    flex items-center justify-center
                                    text-sm font-medium
                                    transition-all duration-300 ease-in-out
                                    transform hover:scale-105
                                    ${isHomePage && currentTheme === 1
                                    ? 'bg-white/20 text-white hover:bg-white/30'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                }
                                `}
                            >
                                {t('Cuentos Princonser', 'Princonser Tales')}
                            </button>
                            {isMenuOpen && (
                                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        {t('Selecciona un libro', 'Select a book')}
                                    </div>
                                    {books.map((book) => (
                                        <button
                                            key={book.id}
                                            onClick={() => handleThemeChange(book.id)}
                                            className={`
                                                w-full px-3 py-2 text-left flex items-center space-x-3
                                                hover:bg-gray-50 transition-colors duration-200
                                                ${currentTheme === book.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}
                                            `}
                                        >
                                            <span className="text-lg">{book.icon}</span>
                                            <span className="text-sm font-medium">{book.label}</span>
                                            {currentTheme === book.id && (
                                                <span className="ml-auto text-blue-500">✓</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Botón ¿Qué es Princonser Kids? con menú desplegable */}
                        <div
                            className="relative"
                            onMouseEnter={() => setIsAboutDropdownOpen(true)}
                            onMouseLeave={() => setIsAboutDropdownOpen(false)}
                        >
                            <button
                                className={`
                                    relative group
                                    px-4 py-2
                                    rounded-full
                                    flex items-center justify-center
                                    text-sm font-medium
                                    transition-all duration-300 ease-in-out
                                    transform hover:scale-105
                                    ${isHomePage && currentTheme === 1
                                        ? 'bg-white/20 text-white hover:bg-white/30'
                                        : 'bg-green-500 text-white hover:bg-green-600'
                                    }
                                `}
                            >
                                {t('¿Qué es Princonser Kids?', 'What is Princonser Kids?')}
                            </button>
                            {isAboutDropdownOpen && (
                                <div className="absolute top-full left-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                    <button
                                        onClick={() => { setIsAboutDropdownOpen(false); navigate('/que-es-princonser'); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-700 text-sm font-medium"
                                    >
                                        {t('¿Qué es Princonser Kids?', 'What is Princonser Kids?')}
                                    </button>
                                    <button
                                        onClick={() => { setIsAboutDropdownOpen(false); navigate('/filosofia-princonser'); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-700 text-sm font-medium"
                                    >
                                        {t('Filosofía Princonser', 'Princonser Philosophy')}
                                    </button>
                                    <button
                                        onClick={() => { setIsAboutDropdownOpen(false); navigate('/curriculum-holistico'); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-700 text-sm font-medium"
                                    >
                                        {t('Currículum Holístico', 'Holistic Curriculum')}
                                    </button>
                                    <button
                                        onClick={() => { setIsAboutDropdownOpen(false); navigate('/metodos-ensenanza'); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-700 text-sm font-medium"
                                    >
                                        {t('Método de Enseñanza', 'Teaching Method')}
                                    </button>
                                    <button
                                        onClick={() => { setIsAboutDropdownOpen(false); navigate('/innovaciones?section=4'); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-700 text-sm font-medium"
                                    >
                                        {t('Gamificación', 'Gamification')}
                                    </button>
                                    <button
                                        onClick={() => { setIsAboutDropdownOpen(false); navigate('/ecosistema-incentivo'); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-700 text-sm font-medium"
                                    >
                                        {t('Ecosistema de incentivos', 'Incentive Ecosystem')}
                                    </button>
                                    <button
                                        onClick={() => { setIsAboutDropdownOpen(false); navigate('/blog-princonser'); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 text-gray-700 text-sm font-medium"
                                    >
                                        {t('Blog Princonser', 'Princonser Blog')}
                                    </button>
                                </div>
                            )}
                        </div>
                        {/* Botón Consejo de Sabios */}
                        <button
                            onClick={() => navigate('/consejo-sabios')}
                            className={`
                                relative group
                                px-4 py-2
                                rounded-full
                                flex items-center justify-center
                                text-sm font-medium
                                transition-all duration-300 ease-in-out
                                transform hover:scale-105
                                ${isHomePage && currentTheme === 1
                                    ? 'bg-white/20 text-white hover:bg-white/30'
                                    : 'bg-purple-500 text-white hover:bg-purple-600'
                                }
                            `}
                        >
                            {t('Círculo de Sabios', 'Circle of Sages')}
                        </button>
                        {/* Botón Explorer */}
                        <button
                            onClick={() => navigate('/explorer')}
                            className={`
                                relative group
                                px-4 py-2
                                rounded-full
                                flex items-center justify-center
                                text-sm font-medium
                                transition-all duration-300 ease-in-out
                                transform hover:scale-105
                                ${isHomePage && currentTheme === 1
                                    ? 'bg-white/20 text-white hover:bg-white/30'
                                    : 'bg-orange-500 text-white hover:bg-orange-600'
                                }
                            `}
                        >
                            {t('Explorer', 'Explorer')}
                        </button>
                    </div>

                    {/* Botones de autenticación y selector de idioma - Desktop */}
                    <div className="hidden lg:flex items-center space-x-2">
                        {/* Selector de idioma */}
                        <button
                            onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                            className={`
                                relative group
                                w-10 h-10
                                rounded-full
                                flex items-center justify-center
                                text-lg
                                transition-all duration-300 ease-in-out
                                transform hover:scale-110
                                ${isHomePage && currentTheme === 1
                                    ? 'bg-white/20 text-white shadow-lg shadow-white/50'
                                    : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                                }
                            `}
                            title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
                        >
                            {language === 'es' ? '🇺🇸' : '🇪🇸'}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                {language === 'es' ? 'English' : 'Español'}
                            </div>
                        </button>
                        <button 
                            onClick={() => {
                                trackCTAClick('Iniciar Sesión', 'Navbar', 'button');
                                navigate('/login');
                            }}
                            className={`px-4 py-2 font-medium transition-colors duration-200 ${
                                isHomePage && currentTheme === 1 
                                    ? 'text-white hover:text-yellow-300' 
                                    : 'text-blue-600 hover:text-blue-800'
                            }`}
                        >
                            {t('Iniciar Sesión', 'Login')}
                        </button>
                        <button 
                            onClick={() => {
                                trackCTAClick('Registrarse', 'Navbar', 'button');
                                navigate('/register');
                            }}
                            className={`px-4 py-2 rounded-full font-medium transition-colors duration-200 ${
                                isHomePage && currentTheme === 1 
                                    ? 'bg-yellow-400 text-gray-800 hover:bg-yellow-300' 
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                        >
                            {t('Registrarse', 'Register')}
                        </button>
                    </div>

                    {/* Menú hamburguesa - Solo visible en móviles */}
                    <div className="lg:hidden flex items-center space-x-2">
                        {/* Selector de idioma móvil */}
                        <button
                            onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                            className={`
                                relative group
                                w-8 h-8
                                rounded-full
                                flex items-center justify-center
                                text-sm
                                transition-all duration-300 ease-in-out
                                transform hover:scale-110
                                ${isHomePage && currentTheme === 1
                                    ? 'bg-white/20 text-white shadow-lg shadow-white/50'
                                    : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                                }
                            `}
                            title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
                        >
                            {language === 'es' ? '🇺🇸' : '🇪🇸'}
                        </button>
                        
                        {/* Botón hamburguesa */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className={`
                                relative group
                                w-10 h-10
                                rounded-full
                                flex items-center justify-center
                                transition-all duration-300 ease-in-out
                                transform hover:scale-105
                                ${isHomePage && currentTheme === 1
                                    ? 'bg-white/20 text-white hover:bg-white/30'
                                    : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                                }
                            `}
                        >
                            <div className="w-5 h-5 flex flex-col justify-center items-center">
                                <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1' : ''}`}></span>
                                <span className={`block w-5 h-0.5 bg-current mt-1 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
                                <span className={`block w-5 h-0.5 bg-current mt-1 transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1' : ''}`}></span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Menú móvil desplegable */}
            {isMobileMenuOpen && (
                <div className="lg:hidden absolute top-full left-0 right-0 bg-white/95 border-t border-gray-200 shadow-lg z-50">
                    <div className="px-4 py-6 space-y-4 mobile-scroll-container max-h-[80vh] overflow-y-auto">
                        {/* Sección de Cuentos */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                {t('Cuentos Princonser', 'Princonser Tales')}
                            </h3>
                            <div className="mobile-nav-scroll">
                                <div className="flex space-x-2 pb-2" style={{ minWidth: 'max-content' }}>
                                    {books.map((book) => (
                                        <button
                                            key={book.id}
                                            onClick={() => {
                                                handleThemeChange(book.id);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className={`
                                                p-3 rounded-lg flex items-center space-x-2 whitespace-nowrap
                                                transition-colors duration-200 flex-shrink-0
                                                ${currentTheme === book.id ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}
                                            `}
                                        >
                                            <span className="text-lg">{book.icon}</span>
                                            <span className="text-xs font-medium text-left">{book.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Sección de Información */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                {t('Información', 'Information')}
                            </h3>
                            <div className="space-y-2">
                                <button
                                    onClick={() => { navigate('/que-es-princonser'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('¿Qué es Princonser Kids?', 'What is Princonser Kids?')}
                                </button>
                                <button
                                    onClick={() => { navigate('/filosofia-princonser'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('Filosofía Princonser', 'Princonser Philosophy')}
                                </button>
                                <button
                                    onClick={() => { navigate('/curriculum-holistico'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('Currículum Holístico', 'Holistic Curriculum')}
                                </button>
                                <button
                                    onClick={() => { navigate('/metodos-ensenanza'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('Método de Enseñanza', 'Teaching Method')}
                                </button>
                                <button
                                    onClick={() => { navigate('/innovaciones?section=4'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('Gamificación', 'Gamification')}
                                </button>
                                <button
                                    onClick={() => { navigate('/ecosistema-incentivo'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('Ecosistema de incentivos', 'Incentive Ecosystem')}
                                </button>
                                <button
                                    onClick={() => { navigate('/blog-princonser'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('Blog Princonser', 'Princonser Blog')}
                                </button>
                            </div>
                        </div>

                        {/* Sección de Comunidad */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                {t('Comunidad', 'Community')}
                            </h3>
                            <div className="space-y-2">
                                <button
                                    onClick={() => { navigate('/consejo-sabios'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('Círculo de Sabios', 'Circle of Sages')}
                                </button>
                                <button
                                    onClick={() => { navigate('/explorer'); setIsMobileMenuOpen(false); }}
                                    className="w-full p-3 text-left rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                >
                                    {t('Explorer', 'Explorer')}
                                </button>
                            </div>
                        </div>

                        {/* Sección de Autenticación */}
                        <div className="pt-4 border-t border-gray-200">
                            <div className="space-y-2">
                                <button 
                                    onClick={() => { 
                                        trackCTAClick('Iniciar Sesión Mobile', 'Navbar Mobile', 'button');
                                        navigate('/login'); 
                                        setIsMobileMenuOpen(false); 
                                    }}
                                    className={`w-full p-3 text-center rounded-lg font-medium transition-colors duration-200 ${
                                        isHomePage && currentTheme === 1 
                                            ? 'bg-white/20 text-white hover:bg-white/30' 
                                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                    }`}
                                >
                                    {t('Iniciar Sesión', 'Login')}
                                </button>
                                <button 
                                    onClick={() => { 
                                        trackCTAClick('Registrarse Mobile', 'Navbar Mobile', 'button');
                                        navigate('/register'); 
                                        setIsMobileMenuOpen(false); 
                                    }}
                                    className={`w-full p-3 text-center rounded-lg font-medium transition-colors duration-200 ${
                                        isHomePage && currentTheme === 1 
                                            ? 'bg-yellow-400 text-gray-800 hover:bg-yellow-300' 
                                            : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                >
                                    {t('Registrarse', 'Register')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay para cerrar menú móvil */}
            {isMobileMenuOpen && (
                <div 
                    className="fixed inset-0 z-40 bg-black/20" 
                    onClick={() => setIsMobileMenuOpen(false)}
                ></div>
            )}

            {/* Overlay para cerrar menú desktop */}
            {isMenuOpen && (
                <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsMenuOpen(false)}
                ></div>
            )}
        </nav>
    );
};

export default Navbar; 